/**
 * @fileoverview O painel de uma pessoa em Equipe e permissões: o perfil de
 * acesso e as exceções, por módulo, com a origem de cada linha (o padrão do
 * perfil, concedida ou negada) e o motivo das travadas.
 *
 * O cálculo é sempre do servidor: cada mudança na tela pede a prévia
 * (POST /access-control/members/:id/preview), que devolve o acesso que a pessoa
 * teria (com as dependências e os tetos) e a recusa que a gravação teria; a
 * linha mexida muda na hora e as outras acompanham a prévia. Salvar mostra o
 * que muda (as sensíveis marcadas) e grava com a versão lida: se outra pessoa
 * salvou antes, o painel relê o acesso de agora. Sair com alterações pergunta
 * antes (a guarda da área de Configurações: o painel é o ?user= do endereço).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MenuItem, Switch, TextField, InputAdornment } from "@material-ui/core";
import { ChevronLeft, ChevronRight, Lock, RotateCcw, Search } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import ModernSelect from "../ModernSelect";
import UnsavedChangesHint from "../UnsavedChangesHint";
import { LoadError, LoadingState } from "../Settings/LoadState";
import { useReportDirty } from "../../hooks/useLeaveGuard";
import { useDebounce } from "../../hooks/useDebounce";
import {
  groupLabel,
  lockLabel,
  normalize,
  originLabel,
  permissionHelp,
  permissionTitle,
  readOnlyLabel,
  roleHelp,
  roleLabel,
  scopeLabel,
} from "./labels";
import PersonAvatar from "./PersonAvatar";
import useTeamAccessStyles from "./styles";

const t = (key, options) => i18n.t(`accessControl.${key}`, options);

const SCOPES = ["none", "own", "queues", "company"];
const VIEW_KEY = "tickets.view";
const PREVIEW_DELAY_MS = 150;

const byPermission = (a, b) => a.permission.localeCompare(b.permission);
const sameOverrides = (a, b) =>
  JSON.stringify([...a].sort(byPermission).map(({ permission, effect, scope }) => [permission, effect, scope || null])) ===
  JSON.stringify([...b].sort(byPermission).map(({ permission, effect, scope }) => [permission, effect, scope || null]));

const draftOf = (detail) => ({ role: detail.role, overrides: detail.overrides.map((item) => ({ ...item })) });

// A linha ligada no acesso calculado (o nível de conversas: ligado quando não é "nenhuma").
const isOn = (access, key) => access.permissions.find((row) => row.key === key)?.on || false;

// O que muda entre o acesso salvo e o proposto: o perfil, o nível de conversas e as permissões.
const changesBetween = (saved, next, catalog) => {
  const keys = catalog.permissions.filter((item) => !item.scoped).map((item) => item.key);
  return {
    role: saved.role !== next.role ? { from: saved.role, to: next.role } : null,
    scope: saved.ticketsView !== next.ticketsView ? { from: saved.ticketsView, to: next.ticketsView } : null,
    gains: keys.filter((key) => !isOn(saved, key) && isOn(next, key)),
    loses: keys.filter((key) => isOn(saved, key) && !isOn(next, key)),
  };
};

const PersonPanel = ({ userId, catalog, onBack, onSaved, onDirtyChange }) => {
  const classes = useTeamAccessStyles();
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("loading");
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  // A prévia que falhou (o código do erro): o rodapé diz o motivo e não salva.
  const [previewFailure, setPreviewFailure] = useState(null);
  // A linha mexida, até a prévia dela chegar.
  const [optimistic, setOptimistic] = useState({});
  const [search, setSearch] = useState("");
  const [onlyCustomized, setOnlyCustomized] = useState(false);
  const [onlySensitive, setOnlySensitive] = useState(false);
  const [open, setOpen] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const previewSeq = useRef(0);
  // O rascunho de agora: a prévia só vale para o rascunho que a pediu (depois de
  // salvar, restaurar ou descartar, uma prévia antiga não volta a mexer nele).
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const definitions = useMemo(
    () => Object.fromEntries(catalog.permissions.map((item) => [item.key, item])),
    [catalog]
  );

  const load = () => {
    setStatus((prev) => (prev === "ready" ? prev : "loading"));
    return api
      .get(`/access-control/members/${userId}`)
      .then(({ data }) => {
        setDetail(data);
        setDraft(draftOf(data));
        setPreview(null);
        setOptimistic({});
        setStatus("ready");
      })
      .catch((err) => {
        setStatus((prev) => (prev === "ready" ? prev : "error"));
        toastError(err);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const dirty = !!detail && !!draft && (draft.role !== detail.role || !sameOverrides(draft.overrides, detail.overrides));
  useReportDirty(dirty, onDirtyChange);

  // A prévia do rascunho (sem mudança, vale o acesso salvo).
  const debouncedDraft = useDebounce(draft, PREVIEW_DELAY_MS);
  useEffect(() => {
    if (!detail || !debouncedDraft) return;
    const changed =
      debouncedDraft.role !== detail.role || !sameOverrides(debouncedDraft.overrides, detail.overrides);
    previewSeq.current += 1;
    const seq = previewSeq.current;
    setPreviewFailure(null);
    if (!changed) {
      setPreview(null);
      setOptimistic({});
      return;
    }
    api
      .post(`/access-control/members/${userId}/preview`, debouncedDraft)
      .then(({ data }) => {
        if (previewSeq.current !== seq || draftRef.current !== debouncedDraft) return;
        setPreview(data);
        setOptimistic({});
        // As exceções que não mudam nada no perfil novo saem (o servidor as tira ao gravar).
        if (!sameOverrides(debouncedDraft.overrides, data.overrides)) {
          setDraft({ ...debouncedDraft, overrides: data.overrides });
        }
      })
      .catch((err) => {
        if (previewSeq.current === seq && draftRef.current === debouncedDraft) {
          setPreviewFailure(err?.response?.data?.error || "preview");
        }
      });
  }, [debouncedDraft, detail, userId]);

  if (status === "loading") return <LoadingState />;
  if (status === "error" || !detail || !draft) {
    return (
      <div className={classes.root}>
        <BackButton classes={classes} onClick={onBack} />
        <LoadError onRetry={load} />
      </div>
    );
  }

  const readOnly = detail.readOnly;
  const shown = dirty && preview ? preview : detail;
  const pending = dirty && (!preview || preview.role !== draft.role || !sameOverrides(preview.overrides, draft.overrides));
  const defaults = new Set(catalog.defaults[draft.role] || []);
  const defaultView = catalog.defaultTicketsView[draft.role];
  const rowOf = (key) => shown.permissions.find((row) => row.key === key);

  const setOverride = (key, override) =>
    setDraft((current) => ({
      ...current,
      overrides: [...current.overrides.filter((item) => item.permission !== key), ...(override ? [override] : [])],
    }));

  // Ligar ou desligar: igual ao padrão do perfil, a exceção sai.
  const setPermission = (key, on) => {
    setOptimistic((prev) => ({ ...prev, [key]: on }));
    setOverride(key, on === defaults.has(key) ? null : { permission: key, effect: on ? "grant" : "deny", scope: null });
  };

  const setScope = (scope) => {
    setOptimistic((prev) => ({ ...prev, [VIEW_KEY]: scope }));
    if (scope === defaultView) setOverride(VIEW_KEY, null);
    else if (scope === "none") setOverride(VIEW_KEY, { permission: VIEW_KEY, effect: "deny", scope: null });
    else setOverride(VIEW_KEY, { permission: VIEW_KEY, effect: "grant", scope });
  };

  const clearOverride = (key) => {
    setOptimistic((prev) => ({ ...prev, [key]: key === VIEW_KEY ? defaultView : defaults.has(key) }));
    setOverride(key, null);
  };

  const discard = () => {
    setDraft(draftOf(detail));
    setPreview(null);
    setOptimistic({});
  };

  // Salvar e restaurar: grava com a versão lida; se outra pessoa salvou antes, relê o de agora.
  const persist = async (request, successKey) => {
    setSaving(true);
    try {
      const { data } = await request();
      setDetail(data);
      setDraft(draftOf(data));
      setPreview(null);
      setConfirm(null);
      toast.success(t(successKey, { name: detail.name }));
      if (onSaved) onSaved();
    } catch (err) {
      setConfirm(null);
      if (err?.response?.data?.error === "ERR_ACCESS_CHANGED") {
        toast.info(t("panel.reloaded"));
        await load();
      } else {
        toastError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  const save = () =>
    persist(() => api.put(`/access-control/members/${userId}`, { ...draft, version: detail.version }), "panel.saved");

  const restore = () =>
    persist(() => api.post(`/access-control/members/${userId}/reset`, { version: detail.version }), "panel.restored");

  // Busca e filtros das linhas.
  const text = normalize(search);
  const filtering = !!text || onlyCustomized || onlySensitive;
  const matches = (key) =>
    (!text || normalize(`${permissionTitle(key)} ${permissionHelp(key)}`).includes(text)) &&
    (!onlyCustomized || rowOf(key)?.origin !== "default") &&
    (!onlySensitive || !!definitions[key]?.sensitive);

  const groups = Object.keys(catalog.groups)
    .map((group) => ({
      group,
      keys: catalog.permissions.filter((item) => item.group === group).map((item) => item.key),
    }))
    .map((entry) => ({ ...entry, visible: entry.keys.filter(matches) }))
    .filter((entry) => entry.visible.length);

  const changes = confirm?.kind === "save" && preview ? changesBetween(detail, preview, catalog) : null;
  // O que impede salvar: a recusa que a gravação teria, ou a prévia que falhou.
  const refusal = dirty ? previewFailure || (!pending ? preview?.refusal : null) : null;

  return (
    <div className={classes.root}>
      <BackButton classes={classes} onClick={onBack} />

      <div className={classes.card}>
        <div className={classes.panelHeader}>
          <div className={classes.panelWho}>
            <PersonAvatar name={detail.name} image={detail.profileImage} />
            <div className={classes.nameWrap}>
              <span className={classes.name}>{detail.name}</span>
              <span className={classes.email}>{detail.email}</span>
            </div>
          </div>
          <div className={classes.panelControls}>
            <ModernSelect
              size="small"
              label={t("panel.role")}
              value={draft.role}
              onChange={(event) => setDraft((current) => ({ ...current, role: event.target.value }))}
              disabled={!!readOnly || saving}
              className={classes.roleField}
              MenuProps={{ disableScrollLock: true }}
            >
              {catalog.roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {roleLabel(role)}
                </MenuItem>
              ))}
            </ModernSelect>
            {!readOnly && (
              <button
                type="button"
                className={classes.secondaryButton}
                onClick={() => setConfirm({ kind: "restore" })}
                disabled={!detail.overrides.length || dirty || saving}
                title={dirty ? t("panel.restoreDirty") : undefined}
              >
                <RotateCcw size={14} strokeWidth={1.8} aria-hidden="true" />
                {t("panel.restore")}
              </button>
            )}
          </div>
        </div>
        <div className={classes.roleHelp}>{roleHelp(draft.role)}</div>
      </div>

      {readOnly && <div className={classes.notice}>{readOnlyLabel(readOnly)}</div>}

      <div className={classes.toolbar}>
        <TextField
          size="small"
          variant="outlined"
          className={classes.search}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("panel.searchPermissions")}
          inputProps={{ "aria-label": t("panel.searchPermissions") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} strokeWidth={1.8} aria-hidden="true" />
              </InputAdornment>
            ),
          }}
        />
        <button
          type="button"
          aria-pressed={onlyCustomized}
          className={[classes.toggleChip, onlyCustomized ? classes.toggleChipOn : ""].filter(Boolean).join(" ")}
          onClick={() => setOnlyCustomized((value) => !value)}
        >
          {t("panel.onlyCustomized")}
        </button>
        <button
          type="button"
          aria-pressed={onlySensitive}
          className={[classes.toggleChip, onlySensitive ? classes.toggleChipOn : ""].filter(Boolean).join(" ")}
          onClick={() => setOnlySensitive((value) => !value)}
        >
          {t("panel.onlySensitive")}
        </button>
      </div>

      {groups.length === 0 && <div className={`${classes.card} ${classes.empty}`}>{t("panel.nothingFound")}</div>}

      {groups.map(({ group, keys, visible }) => {
        const expanded = filtering || !!open[group];
        const onCount = keys.filter((key) => rowOf(key)?.on).length;
        const bodyId = `team-access-group-${group}`;
        return (
          <section key={group} className={classes.group}>
            <button
              type="button"
              className={classes.groupHead}
              aria-expanded={expanded}
              aria-controls={bodyId}
              onClick={() => setOpen((prev) => ({ ...prev, [group]: !expanded }))}
            >
              <ChevronRight
                size={16}
                strokeWidth={1.8}
                aria-hidden="true"
                className={[classes.chevron, expanded ? classes.chevronOpen : ""].filter(Boolean).join(" ")}
              />
              <span className={classes.groupTitle}>{groupLabel(group)}</span>
              <span className={classes.groupCount}>{t("panel.summary", { on: onCount, total: keys.length })}</span>
            </button>
            {expanded && (
              <div id={bodyId}>
                {visible.map((key) => (
                  <PermissionRow
                    key={key}
                    classes={classes}
                    permissionKey={key}
                    definition={definitions[key]}
                    row={rowOf(key)}
                    license={detail.license}
                    expectedOn={(rowOf(key)?.origin === "granted") || (rowOf(key)?.origin === "default" && defaults.has(key))}
                    rowOf={rowOf}
                    ticketsView={shown.ticketsView}
                    maxTicketsView={detail.maxTicketsView}
                    optimistic={optimistic[key]}
                    disabled={!!readOnly || saving}
                    onToggle={setPermission}
                    onScope={setScope}
                    onClear={clearOverride}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}

      {!readOnly && (dirty || saving) && (
        <div className={classes.footer}>
          {refusal ? (
            <span className={classes.footerMessage} role="status">
              {i18n.exists(`backendErrors.${refusal}`) ? i18n.t(`backendErrors.${refusal}`) : t("panel.previewError")}
            </span>
          ) : (
            <UnsavedChangesHint show={dirty} />
          )}
          <span className={classes.spacer} />
          <button type="button" className={classes.secondaryButton} onClick={discard} disabled={saving}>
            {t("panel.discard")}
          </button>
          <button
            type="button"
            className={classes.primaryButton}
            onClick={() => setConfirm({ kind: "save" })}
            disabled={!dirty || pending || !!refusal || saving}
          >
            {t("panel.save")}
          </button>
        </div>
      )}

      <ConfirmationModal
        title={t("panel.confirmTitle", { name: detail.name })}
        open={confirm?.kind === "save"}
        onClose={() => setConfirm(null)}
        onConfirm={save}
        confirmLabel={t("panel.save")}
        confirmLoading={saving}
      >
        {changes && <ChangeSummary classes={classes} changes={changes} definitions={definitions} />}
      </ConfirmationModal>

      <ConfirmationModal
        title={t("panel.restoreTitle")}
        open={confirm?.kind === "restore"}
        onClose={() => setConfirm(null)}
        onConfirm={restore}
        confirmLabel={t("panel.restoreConfirm")}
        confirmLoading={saving}
      >
        {t("panel.restoreBody", { name: detail.name, role: roleLabel(detail.role) })}
      </ConfirmationModal>

    </div>
  );
};

const BackButton = ({ classes, onClick }) => (
  <div>
    <button type="button" className={classes.linkButton} onClick={onClick}>
      <ChevronLeft size={16} strokeWidth={1.8} aria-hidden="true" />
      {t("panel.back")}
    </button>
  </div>
);

const PermissionRow = ({
  classes,
  permissionKey: key,
  definition,
  row,
  license,
  expectedOn,
  rowOf,
  ticketsView,
  maxTicketsView,
  optimistic,
  disabled,
  onToggle,
  onScope,
  onClear,
}) => {
  if (!row || !definition) return null;
  const titleId = `team-access-permission-${key.replace(/\./g, "-")}`;
  // Ligada pelo perfil ou por exceção, mas desligada pela permissão de que depende.
  const blockedBy =
    !row.locked && expectedOn && !row.on
      ? (definition.dependsOn || []).filter((dependency) => !rowOf(dependency)?.on)
      : [];
  const locked = !!row.locked || blockedBy.length > 0;
  const originClass =
    row.origin === "granted" ? classes.granted : row.origin === "denied" ? classes.denied : classes.origin;

  return (
    <div className={classes.permissionRow}>
      <div className={classes.permissionText}>
        <div className={classes.permissionTitleLine}>
          <span className={classes.permissionTitle} id={titleId}>
            {permissionTitle(key)}
          </span>
          {definition.sensitive && <span className={classes.sensitiveTag}>{t("panel.sensitive")}</span>}
        </div>
        <div className={classes.permissionHelp}>{permissionHelp(key)}</div>
        <div className={classes.permissionMeta}>
          <span className={originClass}>{originLabel(row.origin)}</span>
          {row.locked && (
            <span className={classes.lockReason}>
              <Lock size={12} strokeWidth={1.8} aria-hidden="true" />
              {lockLabel(row.locked, license)}
            </span>
          )}
          {blockedBy.length > 0 && (
            <span className={classes.lockReason}>
              {t("panel.dependsOn", { names: blockedBy.map(permissionTitle).join(", ") })}
            </span>
          )}
        </div>
      </div>
      <div className={classes.permissionControl}>
        {row.origin !== "default" && !disabled && (
          <button
            type="button"
            className={classes.iconButton}
            onClick={() => onClear(key)}
            title={t("panel.backToDefault")}
            aria-label={`${t("panel.backToDefault")}: ${permissionTitle(key)}`}
          >
            <RotateCcw size={15} strokeWidth={1.8} aria-hidden="true" />
          </button>
        )}
        {definition.scoped ? (
          <ModernSelect
            size="small"
            value={optimistic ?? ticketsView}
            onChange={(event) => onScope(event.target.value)}
            disabled={disabled || !!row.locked}
            className={classes.scopeSelect}
            SelectDisplayProps={{ "aria-labelledby": titleId }}
            MenuProps={{ disableScrollLock: true }}
          >
            {SCOPES.map((scope) => (
              <MenuItem key={scope} value={scope} disabled={SCOPES.indexOf(scope) > SCOPES.indexOf(maxTicketsView)}>
                {scopeLabel(scope)}
              </MenuItem>
            ))}
          </ModernSelect>
        ) : (
          <Switch
            color="primary"
            checked={optimistic ?? row.on}
            onChange={(event) => onToggle(key, event.target.checked)}
            disabled={disabled || locked}
            inputProps={{ "aria-labelledby": titleId }}
          />
        )}
      </div>
    </div>
  );
};

const ChangeSummary = ({ classes, changes, definitions }) => {
  const item = (key) => (
    <li key={key}>
      {permissionTitle(key)}
      {definitions[key]?.sensitive && (
        <>
          {" "}
          <span className={classes.sensitiveTag}>{t("panel.sensitive")}</span>
        </>
      )}
    </li>
  );
  const nothing = !changes.role && !changes.scope && !changes.gains.length && !changes.loses.length;
  return (
    <div>
      {changes.role && (
        <div>{t("panel.confirmRole", { from: roleLabel(changes.role.from), to: roleLabel(changes.role.to) })}</div>
      )}
      {changes.scope && (
        <div>{t("panel.confirmScope", { from: scopeLabel(changes.scope.from), to: scopeLabel(changes.scope.to) })}</div>
      )}
      {changes.gains.length > 0 && (
        <>
          <p className={classes.changeHeading}>{t("panel.confirmGains")}</p>
          <ul className={classes.changeList}>{changes.gains.map(item)}</ul>
        </>
      )}
      {changes.loses.length > 0 && (
        <>
          <p className={classes.changeHeading}>{t("panel.confirmLoses")}</p>
          <ul className={classes.changeList}>{changes.loses.map(item)}</ul>
        </>
      )}
      {nothing && <div>{t("panel.confirmNothing")}</div>}
    </div>
  );
};

export default PersonPanel;
